import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Users, Pencil, Trash2, Check, X, Shield, Crown, AlertCircle, UserPlus, KeyRound, Mail, RefreshCw, ChevronDown, ChevronUp, Send } from "lucide-react";
import { useBrutalConfirm } from "@/components/BrutalConfirm";

/**
 * League admin → Players management tab.
 *
 * - Rename: cascades naturally (everything is keyed on participant_id; username
 *   is the displayed handle only). Enforces case-sensitive per-league uniqueness.
 * - Delete: removes the row; drafted teams stay locked to the (now-orphaned)
 *   participant_id, so the leaderboard simply skips them. Self-delete and
 *   admin-row delete are blocked by the backend.
 * - Locked tournament freezes all actions (matches Settings lock UX).
 */
export default function ManagePlayersTab({
    leagueId, participants, currentParticipantId, adminParticipantId, isLocked, teamCount, onChanged,
}) {
    // iter87.134 — `window.confirm` is silently blocked on mobile browsers
    // inside a Radix Dialog modal (operator report: "delete user button
    // stopped working"). Swap for the neo-brutalist AlertDialog wrapper.
    const { confirm: brutalConfirm, hostElement: confirmHost } = useBrutalConfirm();
    const [editingId, setEditingId] = useState(null);
    const [draftName, setDraftName] = useState("");
    const [busy, setBusy] = useState(null);
    // Add player dialog state (mirrors site-admin iter31f, scoped to one league).
    const [addOpen, setAddOpen] = useState(false);
    const [addForm, setAddForm] = useState({ username: "", email: "", sendEmail: true });
    const [addBusy, setAddBusy] = useState(false);
    // Per-row "More" expander → reveals email-edit + personal-code controls.
    const [expandedId, setExpandedId] = useState(null);
    const [emailDraft, setEmailDraft] = useState("");
    const [emailEditing, setEmailEditing] = useState(false);
    // Code cache per participant so we don't re-fetch every render.
    const [codeByPid, setCodeByPid] = useState({});

    const beginRename = (p) => {
        setEditingId(p.id);
        setDraftName(p.username);
    };
    const cancelRename = () => {
        setEditingId(null);
        setDraftName("");
    };
    const saveRename = async (p) => {
        const next = draftName.trim();
        if (!next) { toast.error("Username can't be empty"); return; }
        if (next === p.username) { cancelRename(); return; }
        setBusy(p.id);
        try {
            await api.adminRenameParticipant(leagueId, p.id, next);
            toast.success(`Renamed ${p.username} → ${next}`);
            cancelRename();
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Rename failed");
        }
        setBusy(null);
    };

    const handleDelete = async (p) => {
        const ok = await brutalConfirm({
            title: `Remove ${p.username} from the Rivalry?`,
            body:
                "Their drafted teams stay locked to this crewmate slot, " +
                "so the leaderboard will skip them but team history is preserved. " +
                "This cannot be undone.",
            confirmLabel: "Remove crewmate",
            cancelLabel: "Cancel",
            destructive: true,
        });
        if (!ok) return;
        setBusy(p.id);
        try {
            await api.adminRemoveParticipant(leagueId, p.id);
            toast.success(`${p.username} removed`);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Remove failed");
        }
        setBusy(null);
    };

    const sorted = (participants || [])
        .slice()
        .sort((a, b) => {
            // Admin first, then alphabetical case-insensitive
            if (a.id === adminParticipantId) return -1;
            if (b.id === adminParticipantId) return 1;
            return a.username.localeCompare(b.username, undefined, { sensitivity: "base" });
        });

    // Cap math mirrors the backend (_league_invite_cap): min(20, team_count)
    // minus current participants. Pending invites aren't tracked here, so the
    // server is still the final word — but this counter is good enough for UX.
    const cap = Math.min(20, teamCount || 20);
    const remaining = Math.max(0, cap - sorted.length);

    const cancelAdd = () => {
        setAddOpen(false);
        setAddForm({ username: "", email: "", sendEmail: true });
        setAddBusy(false);
    };
    const submitAdd = async () => {
        const username = addForm.username.trim();
        const email = addForm.email.trim();
        if (!username) { toast.error("Username is required"); return; }
        setAddBusy(true);
        try {
            const r = await api.adminAddParticipant(leagueId, {
                username,
                email: email || null,
                send_invite_email: !!(email && addForm.sendEmail),
            });
            toast.success(`Added ${r.participant.username}`);
            if (r.late_join) {
                toast("Heads up — draft already happened, this crewmate has no teams yet.",
                    { icon: "⚠️", duration: 8000 });
            }
            if (email && addForm.sendEmail && r.email_error) {
                toast.error("Player added, but invite email failed: " + r.email_error);
            }
            cancelAdd();
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to add player");
            setAddBusy(false);
        }
    };

    // ---- Per-row expander handlers (email edit + personal code) ----
    const toggleExpand = (p) => {
        if (expandedId === p.id) {
            setExpandedId(null);
            setEmailEditing(false);
            return;
        }
        setExpandedId(p.id);
        setEmailEditing(false);
        setEmailDraft(p.email || "");
    };
    const beginEmailEdit = (p) => {
        setEmailEditing(true);
        setEmailDraft(p.email || "");
    };
    const saveEmail = async (p) => {
        const next = emailDraft.trim().toLowerCase();
        if (!next || !next.includes("@") || !next.includes(".")) {
            toast.error("Enter a valid email");
            return;
        }
        if (next === (p.email || "")) { setEmailEditing(false); return; }
        setBusy(p.id);
        try {
            await api.updateParticipantEmail(p.id, next);
            toast.success(`Email updated for ${p.username}`);
            setEmailEditing(false);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Update failed");
        }
        setBusy(null);
    };
    const fetchCode = async (p) => {
        setBusy(p.id);
        try {
            const r = await api.getPersonalCode(leagueId, p.id);
            setCodeByPid(prev => ({ ...prev, [p.id]: r.personal_code }));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't load code");
        }
        setBusy(null);
    };
    const resendRecovery = async (p) => {
        if (!p.email) { toast.error("Add an email first"); return; }
        // iter87.152 — swap `window.confirm` for `brutalConfirm` so the
        // dialog isn't silently swallowed on iOS Safari inside a Radix
        // modal (same class of fix as iter87.134/.135/.136).
        const ok = await brutalConfirm({
            title: "Send fresh login link?",
            message: `Email a fresh login link to ${p.username} at ${p.email}? They'll get a 30-minute magic link plus their username, personal code, and invite code.`,
            confirmLabel: "Send link",
            cancelLabel: "Cancel",
        });
        if (!ok) return;
        setBusy(p.id);
        try {
            await api.resendParticipantRecovery(leagueId, p.id);
            toast.success(`Login link sent to ${p.email}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't send the email");
        }
        setBusy(null);
    };

    const regenCode = async (p) => {
        // iter87.152 — Two sequential `brutalConfirm` calls replace the
        // original OK/Cancel/OK-Cancel pair of `window.confirm`s. The
        // first asks whether to email the new code (only offered when
        // an email is on file); the second is the "regenerate without
        // emailing" fallback so the operator can still rotate the code
        // for offline delivery. Mobile Safari inside a Radix Dialog
        // silently drops window.confirm — hence the neo-brutalist
        // AlertDialog wrapper.
        let sendEmail = false;
        if (p.email) {
            sendEmail = await brutalConfirm({
                title: "Regenerate & email new code?",
                message: `Regenerate the code for ${p.username}. Their existing code will stop working immediately. Email the new code to ${p.email}?`,
                confirmLabel: "Regenerate & email",
                cancelLabel: "Regenerate without email",
            });
        }
        let proceed = sendEmail;
        if (!proceed) {
            proceed = await brutalConfirm({
                title: "Regenerate without emailing?",
                message: `The old code stops working immediately. Make sure you can share the new code with ${p.username} another way.`,
                confirmLabel: "Regenerate",
                cancelLabel: "Cancel",
                tone: "danger",
            });
        }
        if (!proceed) return;
        setBusy(p.id);
        try {
            const r = await api.regeneratePersonalCode(leagueId, p.id, sendEmail);
            setCodeByPid(prev => ({ ...prev, [p.id]: r.personal_code }));
            toast.success(sendEmail ? `New code emailed to ${p.email}` : "New code generated");
            if (sendEmail && r.email_error) {
                toast.error("Email delivery failed: " + r.email_error);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Regenerate failed");
        }
        setBusy(null);
    };

    return (
        <div className="brutal-card p-5 space-y-4" data-testid="manage-players-tab">
            <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-cyber" />
                <h2 className="font-display font-extrabold text-xl uppercase">Crewmates</h2>
                <span className="pill bg-ink border-[#333] text-[10px] ml-1">{sorted.length}</span>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">
                Rename a crewmate (handle only — picks, scoring, and history all follow the same person) or remove someone who shouldn't be in the Rivalry. Their drafted teams stay locked to the slot, so the leaderboard skips them but the Tournament math stays intact.
            </p>

            {isLocked && (
                <div className="brutal-card-light p-3 text-xs flex items-start gap-2 border-blaze/40" data-testid="players-locked-note">
                    <AlertCircle className="w-4 h-4 text-blaze shrink-0 mt-0.5" />
                    <span>Rivalry is closed or archived — reopen it (or ask a site admin to restore it) from Rivalry Settings to manage crewmates.</span>
                </div>
            )}

            {/* Add Crewmate — mirrors the Site Admin flow. Cap respects min(20, team_count). */}
            <div className="brutal-card-light p-3 space-y-2" data-testid="admin-add-player-card">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                        <UserPlus className="w-4 h-4 text-cyber" />
                        <span className="font-display font-extrabold text-sm uppercase tracking-tight">Add crewmate</span>
                        <span className="pill bg-ink border-[#333] text-[10px]" data-testid="admin-add-player-slots">
                            {remaining} / {cap} slots left
                        </span>
                    </div>
                    {!addOpen && (
                        <button
                            data-testid="admin-add-player-open-btn"
                            onClick={() => setAddOpen(true)}
                            disabled={isLocked || remaining === 0}
                            className="brutal-btn-primary text-xs py-1.5 px-3 disabled:opacity-30"
                            title={remaining === 0 ? "Rivalry is at capacity" : "Add a crewmate to this Rivalry"}
                        >
                            <UserPlus className="w-3.5 h-3.5" /> Add
                        </button>
                    )}
                </div>
                {addOpen && (
                    <div className="space-y-2 pt-1" data-testid="admin-add-player-form">
                        <div>
                            <label className="block text-[10px] uppercase font-bold tracking-wider text-zinc-500 mb-1">
                                Username <span className="text-blaze normal-case font-medium tracking-normal">— case-sensitive</span>
                            </label>
                            <input
                                data-testid="admin-add-player-username-input"
                                autoFocus
                                value={addForm.username}
                                onChange={(e) => setAddForm(f => ({ ...f, username: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === "Enter") submitAdd(); if (e.key === "Escape") cancelAdd(); }}
                                placeholder="player_one"
                                maxLength={40}
                                className="brutal-input w-full py-1.5 text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase font-bold tracking-wider text-zinc-500 mb-1">
                                Email <span className="text-zinc-600 normal-case font-medium tracking-normal">— optional</span>
                            </label>
                            <input
                                data-testid="admin-add-player-email-input"
                                type="email"
                                value={addForm.email}
                                onChange={(e) => setAddForm(f => ({ ...f, email: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === "Enter") submitAdd(); if (e.key === "Escape") cancelAdd(); }}
                                placeholder="player@example.com"
                                className="brutal-input w-full py-1.5 text-sm"
                            />
                        </div>
                        <label className={`flex items-center gap-2 text-xs ${addForm.email.trim() ? "text-zinc-300 cursor-pointer" : "text-zinc-600 cursor-not-allowed"}`}>
                            <input
                                type="checkbox"
                                data-testid="admin-add-player-send-email-checkbox"
                                checked={addForm.sendEmail && !!addForm.email.trim()}
                                disabled={!addForm.email.trim()}
                                onChange={(e) => setAddForm(f => ({ ...f, sendEmail: e.target.checked }))}
                                className="accent-cyber"
                            />
                            Email this crewmate their invite + join link
                        </label>
                        <div className="flex gap-2 pt-1">
                            <button
                                data-testid="admin-add-player-submit-btn"
                                onClick={submitAdd}
                                disabled={addBusy || !addForm.username.trim()}
                                className="brutal-btn-primary text-xs py-1.5 px-3 disabled:opacity-30"
                            >
                                {addBusy ? "Adding…" : "Add crewmate"}
                            </button>
                            <button
                                data-testid="admin-add-player-cancel-btn"
                                onClick={cancelAdd}
                                className="brutal-btn-ghost text-xs py-1.5 px-3"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <ul className="space-y-2" data-testid="players-list">
                {sorted.map(p => {
                    const isAdminRow = p.id === adminParticipantId;
                    const isSelf = p.id === currentParticipantId;
                    const isEditing = editingId === p.id;
                    const isBusy = busy === p.id;
                    const blockDelete = isAdminRow || isSelf;
                    return (
                        <li
                            key={p.id}
                            data-testid={`player-row-${p.id}`}
                            className={`brutal-card-light p-3 flex items-center gap-3 flex-wrap ${isAdminRow ? "border-cyber/40" : ""}`}
                        >
                            {/* Name / edit */}
                            <div className="flex-1 min-w-[160px]">
                                {isEditing ? (
                                    <input
                                        autoFocus
                                        data-testid={`player-rename-input-${p.id}`}
                                        value={draftName}
                                        onChange={(e) => setDraftName(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") saveRename(p);
                                            if (e.key === "Escape") cancelRename();
                                        }}
                                        className="brutal-input w-full text-sm py-1.5"
                                        maxLength={40}
                                    />
                                ) : (
                                    <>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {/* iter84.24 — Crew identity avatar inline with the username. */}
                                        <span
                                            className="text-base leading-none shrink-0"
                                            data-testid={`player-avatar-${p.id}`}
                                            aria-hidden={p.avatar_emoji ? undefined : true}
                                        >
                                            {p.avatar_emoji || <span className="text-zinc-700 text-xs">🙂</span>}
                                        </span>
                                        <span className="font-display font-extrabold tracking-tight" data-testid={`player-username-${p.id}`}>{p.username}</span>
                                        {isAdminRow && (
                                            <span className="pill bg-cyber text-black text-[10px] font-bold flex items-center gap-1">
                                                <Crown className="w-3 h-3" /> ADMIN
                                            </span>
                                        )}
                                        {isSelf && !isAdminRow && (
                                            <span className="pill bg-ink border-[#333] text-[10px] flex items-center gap-1">
                                                <Shield className="w-3 h-3" /> YOU
                                            </span>
                                        )}
                                        {p.is_placeholder && (
                                            <span
                                                data-testid={`player-placeholder-pill-${p.id}`}
                                                title="Invited by email — hasn't claimed their spot yet. They'll pick their real username when they sign in."
                                                className="pill bg-cyber/15 border-cyber text-cyber text-[10px] font-bold flex items-center gap-1"
                                            >
                                                <Mail className="w-3 h-3" /> AWAITING SIGN-IN
                                            </span>
                                        )}
                                        {p.email && (
                                            <span className="text-[10px] text-zinc-500 font-mono truncate max-w-[200px]">{p.email}</span>
                                        )}
                                    </div>
                                    {p.crew_flair && (
                                        <p
                                            data-testid={`player-flair-${p.id}`}
                                            className="text-[10px] text-zinc-500 italic truncate mt-0.5 pl-6"
                                        >
                                            {p.crew_flair}
                                        </p>
                                    )}
                                    </>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-1">
                                {isEditing ? (
                                    <>
                                        <button
                                            data-testid={`player-rename-save-${p.id}`}
                                            onClick={() => saveRename(p)}
                                            disabled={isBusy}
                                            className="brutal-btn-primary text-xs py-1.5 px-3 disabled:opacity-40"
                                            title="Save"
                                        >
                                            <Check className="w-3.5 h-3.5" /> Save
                                        </button>
                                        <button
                                            data-testid={`player-rename-cancel-${p.id}`}
                                            onClick={cancelRename}
                                            disabled={isBusy}
                                            className="brutal-btn-ghost text-xs py-1.5 px-3"
                                            title="Cancel"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            data-testid={`player-rename-${p.id}-btn`}
                                            onClick={() => beginRename(p)}
                                            disabled={isLocked || isBusy}
                                            className="brutal-btn-ghost text-xs py-1.5 px-3 disabled:opacity-30"
                                            title="Rename"
                                        >
                                            <Pencil className="w-3.5 h-3.5" /> Rename
                                        </button>
                                        <button
                                            data-testid={`player-delete-${p.id}-btn`}
                                            onClick={() => handleDelete(p)}
                                            disabled={isLocked || isBusy || blockDelete}
                                            className="brutal-btn-ghost text-xs py-1.5 px-3 disabled:opacity-20"
                                            title={blockDelete
                                                ? (isAdminRow ? "Can't delete the Rivalry admin" : "Can't delete yourself")
                                                : "Remove player"}
                                        >
                                            <Trash2 className="w-3.5 h-3.5 text-heat" />
                                        </button>
                                        <button
                                            data-testid={`player-expand-${p.id}-btn`}
                                            onClick={() => toggleExpand(p)}
                                            className="brutal-btn-ghost text-xs py-1.5 px-2"
                                            title="Email + personal code"
                                        >
                                            {expandedId === p.id
                                                ? <ChevronUp className="w-3.5 h-3.5" />
                                                : <ChevronDown className="w-3.5 h-3.5" />}
                                        </button>
                                    </>
                                )}
                            </div>
                            {/* Per-row expander: email editor + personal-code panel */}
                            {expandedId === p.id && (
                                <div
                                    data-testid={`player-expansion-${p.id}`}
                                    className="basis-full mt-2 pt-3 border-t border-[#222] grid sm:grid-cols-2 gap-3 text-xs"
                                >
                                    {/* Email edit */}
                                    <div className="space-y-1.5">
                                        <p className="text-[10px] uppercase font-bold tracking-wider text-zinc-500 flex items-center gap-1">
                                            <Mail className="w-3 h-3" /> Email
                                        </p>
                                        {emailEditing ? (
                                            <div className="flex gap-1.5">
                                                <input
                                                    autoFocus
                                                    data-testid={`player-email-input-${p.id}`}
                                                    type="email"
                                                    value={emailDraft}
                                                    onChange={(e) => setEmailDraft(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") saveEmail(p);
                                                        if (e.key === "Escape") setEmailEditing(false);
                                                    }}
                                                    placeholder="player@example.com"
                                                    className="brutal-input flex-1 py-1 text-xs"
                                                />
                                                <button
                                                    data-testid={`player-email-save-${p.id}-btn`}
                                                    onClick={() => saveEmail(p)}
                                                    disabled={isBusy}
                                                    className="brutal-btn-primary text-[10px] px-2 py-1"
                                                >
                                                    <Check className="w-3 h-3" />
                                                </button>
                                                <button
                                                    onClick={() => setEmailEditing(false)}
                                                    className="brutal-btn-ghost text-[10px] px-2 py-1"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span
                                                    data-testid={`player-email-display-${p.id}`}
                                                    className={`font-mono ${p.email ? "text-zinc-300" : "text-zinc-600 italic"}`}
                                                >
                                                    {p.email || "no email on file"}
                                                </span>
                                                <button
                                                    data-testid={`player-email-edit-${p.id}-btn`}
                                                    onClick={() => beginEmailEdit(p)}
                                                    disabled={isLocked || isBusy}
                                                    className="brutal-btn-ghost text-[10px] px-2 py-1 disabled:opacity-30"
                                                >
                                                    <Pencil className="w-3 h-3" /> Edit
                                                </button>
                                                <button
                                                    data-testid={`player-resend-recovery-${p.id}-btn`}
                                                    onClick={() => resendRecovery(p)}
                                                    disabled={!p.email || isBusy}
                                                    className="brutal-btn-ghost text-[10px] px-2 py-1 disabled:opacity-30"
                                                    title={p.email
                                                        ? `Email a fresh login link to ${p.email}`
                                                        : "Add an email first"}
                                                >
                                                    <Send className="w-3 h-3" /> Resend login link
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    {/* Personal code controls */}
                                    <div className="space-y-1.5">
                                        <p className="text-[10px] uppercase font-bold tracking-wider text-zinc-500 flex items-center gap-1">
                                            <KeyRound className="w-3 h-3" /> Personal code
                                        </p>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {codeByPid[p.id] ? (
                                                <span
                                                    data-testid={`player-code-value-${p.id}`}
                                                    className="font-mono font-extrabold text-volt text-sm tracking-[0.12em] select-all"
                                                >
                                                    {codeByPid[p.id]}
                                                </span>
                                            ) : (
                                                <button
                                                    data-testid={`player-code-show-${p.id}-btn`}
                                                    onClick={() => fetchCode(p)}
                                                    disabled={isBusy}
                                                    className="brutal-btn-ghost text-[10px] px-2 py-1 disabled:opacity-40"
                                                >
                                                    Show code
                                                </button>
                                            )}
                                            <button
                                                data-testid={`player-code-regen-${p.id}-btn`}
                                                onClick={() => regenCode(p)}
                                                disabled={isLocked || isBusy}
                                                className="brutal-btn-ghost text-[10px] px-2 py-1 disabled:opacity-30"
                                                title="Regenerate (old code stops working immediately)"
                                            >
                                                <RefreshCw className="w-3 h-3" /> Regenerate
                                            </button>
                                            {codeByPid[p.id] && (
                                                <button
                                                    data-testid={`player-code-copy-${p.id}-btn`}
                                                    onClick={() => {
                                                        navigator.clipboard?.writeText(codeByPid[p.id]);
                                                        toast.success("Copied");
                                                    }}
                                                    className="brutal-btn-ghost text-[10px] px-2 py-1"
                                                >
                                                    Copy
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>

            {sorted.length === 0 && (
                <p className="text-xs text-zinc-500 italic" data-testid="players-empty">
                    No crewmates in this Rivalry yet.
                </p>
            )}
            {/* iter87.134 — Mount the AlertDialog host once. Fires on
                delete-crewmate confirmations (see handleDelete). */}
            {confirmHost}
        </div>
    );
}
